import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Upload, ChevronDown, ChevronUp,
  Home as HomeIcon, ImageIcon, Info, Wrench, Truck, Milestone,
  Users2, Handshake, Megaphone, MapPinned, Phone, Layers,
} from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";

const inputCls =
  "w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-md px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-shadow";

const SECTIONS_NAV = [
  { id: "sec-hero", icon: HomeIcon, title: "Hero" },
  { id: "sec-media", icon: ImageIcon, title: "Imej & Video" },
  { id: "sec-about", icon: Info, title: "Tentang Kami" },
  { id: "sec-services", icon: Wrench, title: "Perkhidmatan" },
  { id: "sec-fleet", icon: Truck, title: "Armada" },
  { id: "sec-milestones", icon: Milestone, title: "Pencapaian" },
  { id: "sec-team", icon: Users2, title: "Kepimpinan" },
  { id: "sec-clients", icon: Handshake, title: "Pelanggan" },
  { id: "sec-popup", icon: Megaphone, title: "Iklan Popup" },
  { id: "sec-coverage", icon: MapPinned, title: "Industri & Liputan" },
  { id: "sec-banners", icon: Layers, title: "Latar Halaman" },
  { id: "sec-contact", icon: Phone, title: "Hubungan" },
];

const Section = ({ id, title, icon: Icon, description, children }) => (
  <div id={id} className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-6 lg:p-7 scroll-mt-24 shadow-sm dark:shadow-none transition-colors">
    <div className="flex items-start gap-3 mb-5 pb-4 border-b border-slate-200/70 dark:border-white/5">
      {Icon && (
        <span className="shrink-0 w-9 h-9 rounded-lg bg-orange-500/10 dark:bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
          <Icon size={17} className="text-orange-600 dark:text-orange-400" />
        </span>
      )}
      <div className="min-w-0">
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-lg leading-tight">{title}</h3>
        {description && <p className="text-slate-500 dark:text-slate-500 text-xs mt-1">{description}</p>}
      </div>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Label = ({ children }) => <label className="block text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1.5">{children}</label>;

const ImageField = ({ label, value, onUploaded, testId }) => {
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      onUploaded(data.url);
      toast.success("Imej dimuat naik — klik Simpan Perubahan untuk kekalkan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        {value ? (
          <img src={fileUrl(value)} alt={label} className="h-16 w-24 object-cover rounded-md border border-slate-800" />
        ) : (
          <div className="h-16 w-24 rounded-md border border-dashed border-slate-700 flex items-center justify-center text-slate-600 text-xs">Tiada</div>
        )}
        <label className="cursor-pointer flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-full transition-colors">
          <Upload size={14} /> {uploading ? "Memuat naik..." : "Tukar Imej"}
          <input data-testid={testId} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} />
        </label>
      </div>
    </div>
  );
};

const HeroVideoField = ({ value, onChange }) => {
  const [progress, setProgress] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["mp4", "webm", "mov"].includes(ext)) {
      toast.error("Format video mestilah MP4, WEBM atau MOV");
      e.target.value = "";
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Saiz video melebihi had 50MB");
      e.target.value = "";
      return;
    }
    setProgress(0);
    try {
      const uploadId = crypto.randomUUID();
      const chunkSize = 4 * 1024 * 1024;
      const total = Math.ceil(f.size / chunkSize);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", f.slice(i * chunkSize, (i + 1) * chunkSize), "chunk.bin");
        await api.post("/upload/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", f.name);
      const { data } = await api.post("/upload/complete", fd);
      onChange(data.url);
      toast.success("Video dimuat naik — klik Simpan Perubahan untuk kekalkan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik video");
    } finally {
      setProgress(null);
      e.target.value = "";
    }
  };

  return (
    <div>
      <Label>Video Hero (pilihan — menggantikan imej hero)</Label>
      <div className="flex items-center gap-4 flex-wrap">
        {value ? (
          <video src={fileUrl(value)} className="h-16 w-24 object-cover rounded-md border border-slate-800" muted />
        ) : (
          <div className="h-16 w-24 rounded-md border border-dashed border-slate-700 flex items-center justify-center text-slate-600 text-xs">Tiada</div>
        )}
        <label className="cursor-pointer flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-full transition-colors">
          <Upload size={14} /> {progress !== null ? `Memuat naik... ${progress}%` : "Muat Naik Video"}
          <input data-testid="upload-hero-video" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={onFile} />
        </label>
        {value && (
          <button onClick={() => onChange("")} data-testid="remove-hero-video-btn" className="text-red-400 hover:text-red-300 text-sm font-semibold">
            Buang Video
          </button>
        )}
      </div>
      <p className="text-slate-500 text-xs mt-2">MP4/WEBM/MOV · Maksimum 50MB. Jika video ditetapkan, ia akan dipaparkan menggantikan imej hero di halaman utama.</p>
    </div>
  );
};

const BIO_FIELDS = [
  { key: "position", label: "Jawatan" },
  { key: "gender", label: "Jantina" },
  { key: "age", label: "Umur" },
  { key: "status", label: "Status" },
  { key: "nationality", label: "Warganegara" },
  { key: "race", label: "Bangsa" },
  { key: "religion", label: "Agama" },
  { key: "education", label: "Pendidikan" },
  { key: "institution", label: "Institusi" },
];

const TeamMemberRow = ({ member, index, inputCls, onFieldChange, onBioChange, onPhotoUpload, onRemove }) => {
  const [open, setOpen] = useState(false);
  const bio = member.bio || {};
  return (
    <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40">
      <div className="flex gap-3 items-start">
        <label className="relative cursor-pointer group shrink-0" title="Klik untuk muat naik gambar">
          {member.photo ? (
            <img src={fileUrl(member.photo)} alt={member.name} className="h-11 w-11 rounded-full object-cover object-top border border-slate-700" />
          ) : (
            <div className="h-11 w-11 rounded-full border border-dashed border-slate-600 bg-slate-900 flex items-center justify-center">
              <Upload size={14} className="text-slate-500" />
            </div>
          )}
          <span className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload size={14} className="text-white" />
          </span>
          <input data-testid={`upload-team-photo-avatar-${index}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPhotoUpload} />
        </label>
        <div className="flex-1 grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Nama" value={member.name} onChange={(e) => onFieldChange("name", e.target.value)} data-testid={`team-name-${index}`} />
          <input className={inputCls} placeholder="Jawatan (paparan)" value={member.role} onChange={(e) => onFieldChange("role", e.target.value)} data-testid={`team-role-${index}`} />
        </div>
        <label className="cursor-pointer flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-3.5 py-2.5 rounded-full transition-colors shrink-0 mt-0.5" title="Muat naik gambar ahli">
          <Upload size={13} /> Gambar
          <input data-testid={`upload-team-photo-${index}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPhotoUpload} />
        </label>
        <button onClick={onRemove} className="text-red-400 hover:text-red-300 mt-2.5"><Trash2 size={16} /></button>
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`toggle-bio-${index}`}
        className="mt-3 flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 font-semibold"
        type="button"
      >
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {open ? "Tutup Biodata" : "Edit Biodata"}
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-slate-800 space-y-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {BIO_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-1">{label}</label>
                <input
                  className={inputCls}
                  placeholder={label}
                  value={bio[key] || ""}
                  onChange={(e) => onBioChange(key, e.target.value)}
                  data-testid={`bio-${key}-${index}`}
                />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-1">Ringkasan / Bio Singkat</label>
            <textarea
              rows={3}
              className={inputCls}
              placeholder="Ayat pengenalan singkat tentang ahli ini (akan dipaparkan di bahagian atas popup biodata)"
              value={bio.summary || ""}
              onChange={(e) => onBioChange("summary", e.target.value)}
              data-testid={`bio-summary-${index}`}
            />
          </div>
          <p className="text-slate-500 text-[11px] italic">Info sensitif seperti IC, alamat penuh dan tarikh lahir tidak dipaparkan kepada publik untuk melindungi privasi ahli.</p>
        </div>
      )}
    </div>
  );
};

export const ContentEditor = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("bm");

  useEffect(() => {
    setC(null);
    api.get(`/content?lang=${lang}`).then(({ data }) =>
      setC({
        ...data,
        images: { hero: "", about: "", hero_video: "", ...(data.images || {}) },
        clients: Array.isArray(data.clients) ? data.clients : [],
        popup: { enabled: false, media_url: "", media_type: "image", title: "", subtitle: "", cta_text: "", cta_link: "", show_once: true, ...(data.popup || {}) },
        page_banners: {
          about: { image: "", video: "" },
          team: { image: "", video: "" },
          services: { image: "", video: "" },
          gallery: { image: "", video: "" },
          contact: { image: "", video: "" },
          ...(data.page_banners || {}),
        },
      })
    );
  }, [lang]);

  if (!c) return <p className="text-slate-400">Memuatkan...</p>;

  const set = (path, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setListItem = (listKey, idx, field, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      if (field === null) next[listKey][idx] = value;
      else next[listKey][idx][field] = value;
      return next;
    });
  };

  const addItem = (listKey, item) => setC((prev) => ({ ...prev, [listKey]: [...prev[listKey], item] }));
  const removeItem = (listKey, idx) =>
    setC((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== idx) }));

  const uploadTeamPhoto = async (e, i) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setListItem("team", i, "photo", data.url);
      toast.success("Gambar dimuat naik — klik Simpan Perubahan untuk kekalkan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      e.target.value = "";
    }
  };

  const uploadClientLogo = async (e, i) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setListItem("clients", i, "logo", data.url);
      toast.success("Logo dimuat naik — klik Simpan Perubahan untuk kekalkan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik logo");
    } finally {
      e.target.value = "";
    }
  };

  const uploadPopupImage = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      set("popup.media_url", data.url);
      set("popup.media_type", "image");
      toast.success("Gambar popup dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      e.target.value = "";
    }
  };

  const uploadPopupVideo = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["mp4", "webm", "mov"].includes(ext)) {
      toast.error("Format video: MP4, WEBM atau MOV");
      e.target.value = "";
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Saiz video melebihi had 50MB");
      e.target.value = "";
      return;
    }
    try {
      const uploadId = crypto.randomUUID();
      const chunkSize = 4 * 1024 * 1024;
      const total = Math.ceil(f.size / chunkSize);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", f.slice(i * chunkSize, (i + 1) * chunkSize), "chunk.bin");
        await api.post("/upload/chunk", fd);
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", f.name);
      const { data } = await api.post("/upload/complete", fd);
      set("popup.media_url", data.url);
      set("popup.media_type", "video");
      toast.success("Video popup dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik video");
    } finally {
      e.target.value = "";
    }
  };

  const uploadBannerImage = async (e, page) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      set(`page_banners.${page}.image`, data.url);
      set(`page_banners.${page}.video`, "");
      toast.success("Gambar banner dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      e.target.value = "";
    }
  };

  const uploadServiceImage = async (e, i) => {
    const f = e.target.files[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setListItem("services", i, "image", data.url);
      setListItem("services", i, "video", "");
      toast.success("Gambar perkhidmatan dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik");
    } finally {
      e.target.value = "";
    }
  };

  const uploadServiceVideo = async (e, i) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["mp4", "webm", "mov"].includes(ext)) {
      toast.error("Format video: MP4, WEBM atau MOV");
      e.target.value = "";
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Saiz video melebihi had 50MB");
      e.target.value = "";
      return;
    }
    try {
      const uploadId = crypto.randomUUID();
      const chunkSize = 4 * 1024 * 1024;
      const total = Math.ceil(f.size / chunkSize);
      for (let idx = 0; idx < total; idx++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", idx);
        fd.append("file", f.slice(idx * chunkSize, (idx + 1) * chunkSize), "chunk.bin");
        await api.post("/upload/chunk", fd);
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", f.name);
      const { data } = await api.post("/upload/complete", fd);
      setListItem("services", i, "video", data.url);
      setListItem("services", i, "image", "");
      toast.success("Video perkhidmatan dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik video");
    } finally {
      e.target.value = "";
    }
  };

  const uploadBannerVideo = async (e, page) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["mp4", "webm", "mov"].includes(ext)) {
      toast.error("Format video: MP4, WEBM atau MOV");
      e.target.value = "";
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Saiz video melebihi had 50MB");
      e.target.value = "";
      return;
    }
    try {
      const uploadId = crypto.randomUUID();
      const chunkSize = 4 * 1024 * 1024;
      const total = Math.ceil(f.size / chunkSize);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", f.slice(i * chunkSize, (i + 1) * chunkSize), "chunk.bin");
        await api.post("/upload/chunk", fd);
      }
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", f.name);
      const { data } = await api.post("/upload/complete", fd);
      set(`page_banners.${page}.video`, data.url);
      set(`page_banners.${page}.image`, "");
      toast.success("Video banner dimuat naik — klik Simpan Perubahan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat naik video");
    } finally {
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, ...data } = c;
      await api.put(`/content?lang=${lang}`, { data });
      toast.success("Kandungan laman berjaya dikemas kini!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-24" data-testid="content-editor">
      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-4 flex items-center gap-4 flex-wrap mb-6 shadow-sm dark:shadow-none">
        <span className="text-slate-700 dark:text-slate-300 text-sm font-semibold">Bahasa kandungan:</span>
        <div className="flex rounded-full border border-slate-300 dark:border-slate-700 overflow-hidden text-sm font-bold">
          <button
            onClick={() => setLang("bm")}
            data-testid="editor-lang-bm"
            className={`px-5 py-2 transition-colors ${lang === "bm" ? "bg-orange-600 text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
          >
            Bahasa Melayu
          </button>
          <button
            onClick={() => setLang("en")}
            data-testid="editor-lang-en"
            className={`px-5 py-2 transition-colors ${lang === "en" ? "bg-orange-600 text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
          >
            English
          </button>
        </div>
        <span className="text-slate-500 text-xs">Imej &amp; video dikongsi antara kedua-dua bahasa.</span>
      </div>

      <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-8">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-3 shadow-sm dark:shadow-none" aria-label="Navigasi editor">
            <p className="text-slate-500 dark:text-slate-500 text-[10px] font-bold uppercase tracking-widest px-3 pt-2 pb-3">Seksyen</p>
            <ul className="space-y-1">
              {SECTIONS_NAV.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`nav-${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(s.id);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/70 text-sm transition-colors"
                    >
                      <Icon size={15} className="text-orange-500 shrink-0" />
                      <span className="truncate">{s.title}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <div className="space-y-6">

      <Section id="sec-hero" icon={HomeIcon} title="Hero (Halaman Utama)" description="Tajuk, subtajuk dan butang CTA yang muncul di banner utama laman.">
        <div><Label>Tajuk Utama</Label>
          <input data-testid="edit-hero-title" className={inputCls} value={c.hero.title} onChange={(e) => set("hero.title", e.target.value)} /></div>
        <div><Label>Subtajuk</Label>
          <textarea data-testid="edit-hero-subtitle" rows={2} className={inputCls} value={c.hero.subtitle} onChange={(e) => set("hero.subtitle", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Slogan (Badge)</Label>
            <input className={inputCls} value={c.hero.badge} onChange={(e) => set("hero.badge", e.target.value)} /></div>
          <div><Label>Butang CTA Utama</Label>
            <input className={inputCls} value={c.hero.cta_primary} onChange={(e) => set("hero.cta_primary", e.target.value)} /></div>
        </div>
      </Section>

      <Section id="sec-media" icon={ImageIcon} title="Imej & Video Laman" description="Muat naik imej hero, imej Tentang Kami dan video hero (pilihan).">
        <div className="grid sm:grid-cols-2 gap-6">
          <ImageField label="Imej Hero (Halaman Utama)" value={c.images?.hero} onUploaded={(url) => set("images.hero", url)} testId="upload-hero-image" />
          <ImageField label="Imej Tentang Kami" value={c.images?.about} onUploaded={(url) => set("images.about", url)} testId="upload-about-image" />
        </div>
        <div className="pt-2 border-t border-slate-800 mt-4">
          <HeroVideoField value={c.images?.hero_video} onChange={(url) => set("images.hero_video", url)} />
        </div>
        {(c.images?.hero || c.images?.hero_video) && (
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-500 whitespace-nowrap">Kedudukan Hero: Atas</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={c.images?.hero_pos_y ?? 50}
              data-testid="hero-pos-slider"
              onChange={(e) => set("images.hero_pos_y", Number(e.target.value))}
              className="flex-1 accent-orange-600 cursor-pointer"
            />
            <span className="text-xs text-slate-500 whitespace-nowrap">Bawah</span>
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 tabular-nums w-10 text-right">{c.images?.hero_pos_y ?? 50}%</span>
          </div>
        )}
      </Section>

      <Section id="sec-about" icon={Info} title="Tentang Kami" description="Cerita ringkas syarikat, misi dan visi.">
        <div><Label>Tajuk</Label>
          <input className={inputCls} value={c.about.title} onChange={(e) => set("about.title", e.target.value)} /></div>
        <div><Label>Teks Pengenalan</Label>
          <textarea data-testid="edit-about-text" rows={4} className={inputCls} value={c.about.text} onChange={(e) => set("about.text", e.target.value)} /></div>
        <div><Label>Misi</Label>
          <textarea rows={2} className={inputCls} value={c.about.mission} onChange={(e) => set("about.mission", e.target.value)} /></div>
        <div><Label>Visi</Label>
          <textarea rows={2} className={inputCls} value={c.about.vision} onChange={(e) => set("about.vision", e.target.value)} /></div>
      </Section>

      <Section id="sec-services" icon={Wrench} title="Perkhidmatan" description="Setiap perkhidmatan boleh ada gambar sendiri, 3 mata utama, dan butang CTA. Layout paparan awam alternatif kiri-kanan.">
        {c.services.map((s, i) => (
          <div key={i} className="border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 rounded-xl p-4 space-y-3">
            <div className="flex gap-3 items-start flex-wrap">
              <div className="shrink-0">
                {s.video ? (
                  <video src={fileUrl(s.video)} className="h-16 w-24 rounded-md object-cover border border-slate-300 dark:border-slate-700 bg-black" muted />
                ) : s.image ? (
                  <img src={fileUrl(s.image)} alt={s.name} className="h-16 w-24 rounded-md object-cover border border-slate-300 dark:border-slate-700" />
                ) : (
                  <div className="h-16 w-24 rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center text-slate-400 text-[10px] text-center px-1">Tiada media</div>
                )}
              </div>
              <div className="flex-1 min-w-[200px] grid sm:grid-cols-2 gap-3">
                <input data-testid={`edit-service-name-${i}`} className={inputCls} placeholder="Nama perkhidmatan" value={s.name} onChange={(e) => setListItem("services", i, "name", e.target.value)} />
                <input className={inputCls} placeholder="Keterangan ringkas" value={s.desc || ""} onChange={(e) => setListItem("services", i, "desc", e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <label className="cursor-pointer flex items-center gap-1.5 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-full transition-colors">
                  <Upload size={11} /> Gambar
                  <input data-testid={`upload-service-image-${i}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => uploadServiceImage(e, i)} />
                </label>
                <label className="cursor-pointer flex items-center gap-1.5 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-full transition-colors">
                  <Upload size={11} /> Video
                  <input data-testid={`upload-service-video-${i}`} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadServiceVideo(e, i)} />
                </label>
                {(s.image || s.video) && (
                  <button
                    type="button"
                    onClick={() => { setListItem("services", i, "image", ""); setListItem("services", i, "video", ""); }}
                    data-testid={`remove-service-media-${i}`}
                    className="text-red-500 hover:text-red-400 text-[11px] font-semibold"
                  >
                    Buang media
                  </button>
                )}
              </div>
              <button onClick={() => removeItem("services", i)} className="text-red-500 hover:text-red-400 mt-2.5 shrink-0" data-testid={`remove-service-${i}`}><Trash2 size={16} /></button>
            </div>
            <div>
              <Label>3 Mata Utama (satu setiap baris)</Label>
              <textarea
                data-testid={`service-bullets-${i}`}
                rows={3}
                className={inputCls}
                placeholder="Contoh:&#10;Pembungkusan profesional&#10;Insurans barangan disediakan&#10;Kru berpengalaman"
                value={(s.bullets || []).join("\n")}
                onChange={(e) => setListItem("services", i, "bullets", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Teks Butang CTA</Label>
                <input data-testid={`service-cta-text-${i}`} className={inputCls} placeholder="Minta Sebut Harga" value={s.cta_text || ""} onChange={(e) => setListItem("services", i, "cta_text", e.target.value)} />
              </div>
              <div>
                <Label>Pautan CTA</Label>
                <input data-testid={`service-cta-link-${i}`} className={inputCls} placeholder="/hubungi atau https://..." value={s.cta_link || ""} onChange={(e) => setListItem("services", i, "cta_link", e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        <button onClick={() => addItem("services", { name: "", desc: "", image: "", video: "", bullets: [], cta_text: "Minta Sebut Harga", cta_link: "/hubungi" })} data-testid="add-service-btn" className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-semibold"><Plus size={15} /> Tambah Perkhidmatan</button>
      </Section>

      <Section id="sec-fleet" icon={Truck} title="Armada (Jenis Lori)" description="Jenis-jenis lori dalam armada TMG.">
        {c.fleet.map((f, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="flex-1 grid sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Nama lori" value={f.name} onChange={(e) => setListItem("fleet", i, "name", e.target.value)} />
              <input className={inputCls} placeholder="Keterangan" value={f.desc} onChange={(e) => setListItem("fleet", i, "desc", e.target.value)} />
            </div>
            <button onClick={() => removeItem("fleet", i)} className="text-red-400 hover:text-red-300 mt-2.5"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("fleet", { name: "", desc: "" })} className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-semibold"><Plus size={15} /> Tambah Lori</button>
      </Section>

      <Section id="sec-milestones" icon={Milestone} title="Pencapaian Syarikat" description="Timeline pencapaian sejak tahun ke tahun.">
        {c.milestones.map((m, i) => (
          <div key={i} className="flex gap-3 items-start">
            <input className={`${inputCls} w-24`} placeholder="Tahun" value={m.year} onChange={(e) => setListItem("milestones", i, "year", e.target.value)} />
            <input className={inputCls} placeholder="Keterangan" value={m.text} onChange={(e) => setListItem("milestones", i, "text", e.target.value)} />
            <button onClick={() => removeItem("milestones", i)} className="text-red-400 hover:text-red-300 mt-2.5"><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("milestones", { year: "", text: "" })} className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-semibold"><Plus size={15} /> Tambah Pencapaian</button>
      </Section>

      <Section id="sec-team" icon={Users2} title="Barisan Kepimpinan" description="Foto & biodata ahli kepimpinan yang dipaparkan di halaman Pasukan Kami.">
        <p className="text-slate-500 text-xs -mt-1">Klik bulatan gambar atau butang "Gambar" untuk muat naik foto ahli. Klik "Edit Biodata" untuk isi maklumat lanjut yang akan muncul dalam popup di laman publik. Jangan lupa klik Simpan Perubahan.</p>
        {c.team.map((t, i) => (
          <TeamMemberRow
            key={i}
            member={t}
            index={i}
            inputCls={inputCls}
            onFieldChange={(field, val) => setListItem("team", i, field, val)}
            onBioChange={(field, val) => {
              const bio = { ...(t.bio || {}), [field]: val };
              setListItem("team", i, "bio", bio);
            }}
            onPhotoUpload={(e) => uploadTeamPhoto(e, i)}
            onRemove={() => removeItem("team", i)}
          />
        ))}
        <button onClick={() => addItem("team", { name: "", role: "", photo: "", bio: { position: "", gender: "", nationality: "", race: "", religion: "", education: "", institution: "", age: "", status: "", summary: "" } })} className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-semibold"><Plus size={15} /> Tambah Ahli</button>
      </Section>

      <Section id="sec-clients" icon={Handshake} title="Pelanggan Kami (Logo Marquee)" description="Logo pelanggan yang berjalan (auto-scroll) di halaman utama.">
        <p className="text-slate-500 text-xs -mt-1">Logo pelanggan akan muncul dalam carousel auto-scroll di halaman utama. Muat naik logo (transparent PNG dicadangkan). Senarai dikongsi antara BM & EN.</p>
        {(c.clients || []).map((cl, i) => (
          <div key={i} className="flex gap-3 items-center bg-slate-950/40 border border-slate-800 rounded-lg p-2.5">
            <label className="relative cursor-pointer group shrink-0" title="Klik untuk muat naik logo">
              {cl.logo ? (
                <img src={fileUrl(cl.logo)} alt={cl.name} className="h-12 w-12 rounded-md object-contain bg-white border border-slate-700" />
              ) : (
                <div className="h-12 w-12 rounded-md border border-dashed border-slate-600 bg-slate-900 flex items-center justify-center">
                  <Upload size={14} className="text-slate-500" />
                </div>
              )}
              <span className="absolute inset-0 rounded-md bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Upload size={14} className="text-white" />
              </span>
              <input data-testid={`upload-client-logo-${i}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={(e) => uploadClientLogo(e, i)} />
            </label>
            <input
              data-testid={`client-name-${i}`}
              className={`${inputCls} flex-1`}
              placeholder="Nama pelanggan / institusi"
              value={cl.name || ""}
              onChange={(e) => setListItem("clients", i, "name", e.target.value)}
            />
            <label className="cursor-pointer flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-2 rounded-full transition-colors shrink-0" title="Muat naik logo">
              <Upload size={13} /> Logo
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={(e) => uploadClientLogo(e, i)} />
            </label>
            <button onClick={() => removeItem("clients", i)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`remove-client-${i}`}><Trash2 size={16} /></button>
          </div>
        ))}
        <button onClick={() => addItem("clients", { name: "", logo: "" })} data-testid="add-client-btn" className="flex items-center gap-1.5 text-orange-500 hover:text-orange-400 text-sm font-semibold"><Plus size={15} /> Tambah Pelanggan</button>
      </Section>

      <Section id="sec-popup" icon={Megaphone} title="Iklan Popup Halaman Utama" description="Popup promosi yang muncul auto bila pelawat masuk halaman utama.">
        <p className="text-slate-500 text-xs -mt-1">Popup akan muncul secara automatik apabila pelawat masuk ke halaman utama. Sesuai untuk promosi, pengumuman atau iklan produk. Setting dikongsi antara BM & EN.</p>

        <div className="flex items-center justify-between bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3">
          <div>
            <p className="text-white font-semibold text-sm">Aktifkan Popup Auto</p>
            <p className="text-slate-500 text-xs mt-0.5">Popup keluar automatik 0.8s selepas pelawat masuk laman utama.</p>
          </div>
          <button
            type="button"
            data-testid="toggle-popup-enabled"
            onClick={() => set("popup.enabled", !c.popup?.enabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${c.popup?.enabled ? "bg-orange-600" : "bg-slate-700"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${c.popup?.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div className="flex items-center justify-between bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3">
          <div>
            <p className="text-white font-semibold text-sm">Papar Sekali Sahaja Per Sesi</p>
            <p className="text-slate-500 text-xs mt-0.5">Bila ON, pelawat sama tak akan nampak popup lagi selagi tab pelayar tak ditutup.</p>
          </div>
          <button
            type="button"
            data-testid="toggle-popup-showonce"
            onClick={() => set("popup.show_once", !c.popup?.show_once)}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${c.popup?.show_once ? "bg-orange-600" : "bg-slate-700"}`}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${c.popup?.show_once ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div>
          <Label>Media Popup (Gambar atau Video)</Label>
          <div className="flex items-start gap-4 flex-wrap">
            {c.popup?.media_url ? (
              c.popup?.media_type === "video" || /\.(mp4|webm|mov)$/i.test(c.popup.media_url) ? (
                <video src={fileUrl(c.popup.media_url)} className="h-28 w-40 object-cover rounded-md border border-slate-800 bg-black" muted />
              ) : (
                <img src={fileUrl(c.popup.media_url)} alt="popup" className="h-28 w-40 object-cover rounded-md border border-slate-800" />
              )
            ) : (
              <div className="h-28 w-40 rounded-md border border-dashed border-slate-700 flex items-center justify-center text-slate-600 text-xs text-center px-2">Tiada media</div>
            )}
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-full transition-colors">
                <Upload size={14} /> Muat Naik Gambar
                <input data-testid="upload-popup-image" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadPopupImage} />
              </label>
              <label className="cursor-pointer flex items-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-full transition-colors">
                <Upload size={14} /> Muat Naik Video (maks 50MB)
                <input data-testid="upload-popup-video" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadPopupVideo} />
              </label>
              {c.popup?.media_url && (
                <button
                  type="button"
                  onClick={() => { set("popup.media_url", ""); set("popup.media_type", "image"); }}
                  data-testid="remove-popup-media"
                  className="text-red-400 hover:text-red-300 text-xs font-semibold text-left px-1"
                >
                  Buang media
                </button>
              )}
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-2">Cadangan: gambar segi empat sama (contoh 800×800px) atau video pendek MP4 muted looping.</p>
        </div>

        <div><Label>Tajuk Popup (pilihan)</Label>
          <input data-testid="popup-title" className={inputCls} placeholder="Contoh: Promosi Musim Perayaan!" value={c.popup?.title || ""} onChange={(e) => set("popup.title", e.target.value)} /></div>
        <div><Label>Subtajuk / Keterangan (pilihan)</Label>
          <textarea data-testid="popup-subtitle" rows={3} className={inputCls} placeholder="Contoh: Dapatkan diskaun 20% untuk semua perkhidmatan penghantaran sepanjang bulan ini." value={c.popup?.subtitle || ""} onChange={(e) => set("popup.subtitle", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Teks Butang CTA (pilihan)</Label>
            <input data-testid="popup-cta-text" className={inputCls} placeholder="Contoh: Hubungi Sekarang" value={c.popup?.cta_text || ""} onChange={(e) => set("popup.cta_text", e.target.value)} /></div>
          <div><Label>Pautan Butang CTA (pilihan)</Label>
            <input data-testid="popup-cta-link" className={inputCls} placeholder="/hubungi atau https://..." value={c.popup?.cta_link || ""} onChange={(e) => set("popup.cta_link", e.target.value)} /></div>
        </div>
      </Section>

      <Section id="sec-coverage" icon={MapPinned} title="Industri & Liputan" description="Industri yang dilayani dan negeri liputan.">
        <div><Label>Industri (asingkan dengan koma)</Label>
          <textarea rows={2} className={inputCls} value={c.industries.join(", ")} onChange={(e) => set("industries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
        <div><Label>Kawasan Liputan (asingkan dengan koma)</Label>
          <textarea rows={2} className={inputCls} value={c.coverage.join(", ")} onChange={(e) => set("coverage", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></div>
      </Section>

      <Section id="sec-banners" icon={Layers} title="Latar Halaman (Banner)" description="Muat naik gambar atau video sebagai latar banner atas setiap halaman. Kosongkan untuk guna latar biru default. Video akan autoplay muted looping.">
        <p className="text-slate-500 dark:text-slate-500 text-xs -mt-1">Cadangan gambar: landskap 1920×600px, format JPG/PNG. Video: MP4 pendek 5-15 saat, saiz &lt; 50MB.</p>
        {[
          { key: "about", label: "Tentang Kami (/tentang-kami)" },
          { key: "team", label: "Pasukan Kami (/pasukan-kami)" },
          { key: "services", label: "Perkhidmatan (/perkhidmatan)" },
          { key: "gallery", label: "Galeri (/galeri)" },
          { key: "contact", label: "Hubungi (/hubungi)" },
        ].map(({ key, label }) => {
          const banner = c.page_banners?.[key] || { image: "", video: "" };
          return (
            <div key={key} className="border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 rounded-xl p-4 flex items-center gap-4 flex-wrap">
              <div className="min-w-[180px]">
                <p className="text-slate-900 dark:text-white font-semibold text-sm">{label}</p>
                <p className="text-slate-500 text-xs mt-0.5">
                  {banner.video ? "Video aktif" : banner.image ? "Gambar aktif" : "Guna default"}
                </p>
              </div>
              <div className="shrink-0">
                {banner.video ? (
                  <video src={fileUrl(banner.video)} className="h-20 w-36 object-cover rounded-md border border-slate-300 dark:border-slate-700 bg-black" style={{ objectPosition: `center ${banner.pos_y ?? 50}%` }} muted />
                ) : banner.image ? (
                  <img src={fileUrl(banner.image)} alt={key} className="h-20 w-36 object-cover rounded-md border border-slate-300 dark:border-slate-700" style={{ objectPosition: `center ${banner.pos_y ?? 50}%` }} />
                ) : (
                  <div className="h-20 w-36 rounded-md border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-400 text-[10px] text-center px-2">Tiada media</div>
                )}
              </div>
              <div className="flex flex-col gap-2 ml-auto">
                <label className="cursor-pointer flex items-center gap-2 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-2 rounded-full transition-colors">
                  <Upload size={12} /> Muat Naik Gambar
                  <input data-testid={`upload-banner-image-${key}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => uploadBannerImage(e, key)} />
                </label>
                <label className="cursor-pointer flex items-center gap-2 text-xs bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-2 rounded-full transition-colors">
                  <Upload size={12} /> Muat Naik Video
                  <input data-testid={`upload-banner-video-${key}`} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadBannerVideo(e, key)} />
                </label>
              </div>
              {(banner.image || banner.video) && (
                <button
                  type="button"
                  onClick={() => { set(`page_banners.${key}.image`, ""); set(`page_banners.${key}.video`, ""); }}
                  data-testid={`remove-banner-${key}`}
                  className="text-red-500 hover:text-red-400 text-xs font-semibold"
                >
                  Buang
                </button>
              )}
              {(banner.image || banner.video) && (
                <div className="w-full flex items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-800">
                  <span className="text-xs text-slate-500 whitespace-nowrap">Kedudukan: Atas</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={banner.pos_y ?? 50}
                    data-testid={`banner-pos-${key}`}
                    onChange={(e) => set(`page_banners.${key}.pos_y`, Number(e.target.value))}
                    className="flex-1 accent-orange-600 cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 whitespace-nowrap">Bawah</span>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300 tabular-nums w-10 text-right">{banner.pos_y ?? 50}%</span>
                </div>
              )}
            </div>
          );
        })}
      </Section>

      <Section id="sec-contact" icon={Phone} title="Maklumat Hubungan" description="Alamat, telefon, emel, laman web, Facebook.">
        <div><Label>Alamat</Label>
          <input data-testid="edit-contact-address" className={inputCls} value={c.contact.address} onChange={(e) => set("contact.address", e.target.value)} /></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Telefon</Label>
            <input data-testid="edit-contact-phone" className={inputCls} value={c.contact.phone} onChange={(e) => set("contact.phone", e.target.value)} /></div>
          <div><Label>Emel 1</Label>
            <input data-testid="edit-contact-email" className={inputCls} value={c.contact.email} onChange={(e) => set("contact.email", e.target.value)} /></div>
          <div><Label>Emel 2 (pilihan)</Label>
            <input data-testid="edit-contact-email2" className={inputCls} placeholder="cth: logistics@tanjungmarinaglobal.com" value={c.contact.email2 || ""} onChange={(e) => set("contact.email2", e.target.value)} /></div>
          <div><Label>Laman Web</Label>
            <input className={inputCls} value={c.contact.website} onChange={(e) => set("contact.website", e.target.value)} /></div>
        </div>
        <div><Label>Pautan Facebook (pilihan)</Label>
          <input data-testid="edit-contact-facebook" className={inputCls} placeholder="https://facebook.com/namapage" value={c.contact.facebook || ""} onChange={(e) => set("contact.facebook", e.target.value)} /></div>
        <div><Label>WhatsApp (nombor ATAU pautan wa.me)</Label>
          <input data-testid="edit-contact-whatsapp" className={inputCls} placeholder="cth: 0122742244 atau https://wa.me/message/XXXXXXX" value={c.contact.whatsapp || ""} onChange={(e) => set("contact.whatsapp", e.target.value)} />
          <p className="text-xs text-slate-400 mt-1">Boleh letak nombor telefon ATAU pautan penuh WhatsApp Business (https://wa.me/message/...). Butang hijau akan muncul di penjuru kanan bawah laman web. Kosongkan untuk sembunyikan butang.</p></div>
      </Section>
        </div>
      </div>

      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content-btn"
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-full shadow-2xl shadow-orange-600/30 transition-all hover:-translate-y-0.5"
        >
          <Save size={18} /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </div>
    </div>
  );
};
